import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileAudio,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { useTheme } from '../zustand/ThemeStore';
import { useUserStore } from '../zustand/UserStore';
import { useAuthorization } from '../hooks/useAuthorization';

interface AnalysisFile {
  id: string;
  filename: string;
  type: 'transcript' | 'audio';
  customerName: string;
  status: 'completed' | 'processing' | 'failed' | 'pending';
}

interface AnalysisData {
  analysisId: string;
  userId: string;
  userName: string;
  userRole: string;
  userAvatar?: string;
  totalFiles: number;
  files: AnalysisFile[];
}

const AnalysisResultsPage: React.FC = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();
  const isDarkMode = useTheme((state) => state.theme);
  const currentUser = useUserStore((state) => state.user);
  const auth = useAuthorization();
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile] = useState<string | null>(null);
  const [loadingFile] = useState<string | null>(null);

  useEffect(() => {
    // Try to get analysis data from sessionStorage first
    const cachedData = sessionStorage.getItem(`analysis_result_${resultId}`);
    if (cachedData) {
      try {
        const parsedData = JSON.parse(cachedData);
        
        // Security check: regular users can only view their own analysis results
        if (!auth.canAccessAnalysisDashboard && parsedData.userId !== currentUser?.uid) {
          console.warn('Unauthorized access to analysis results');
          navigate('/analysis');
          return;
        }
        
        setAnalysisData(parsedData);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Failed to parse cached analysis data:', error);
      }
    }

    // If no cached data, redirect back to analysis dashboard
    // In a real app, you might want to fetch from API here
    setLoading(false);
    // Optionally redirect to analysis dashboard if no cached data
    // navigate('/analysis');
  }, [resultId, navigate, auth.canAccessAnalysisDashboard, currentUser?.uid]);

  const handleFileClick = async (file: AnalysisFile) => {
    navigate(`/analysis/${file.id}`);
  };

  const handleBackToAnalysis = () => {
    navigate('/analysis');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'processing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return isDarkMode ? 'text-green-400' : 'text-green-600';
      case 'processing':
        return isDarkMode ? 'text-blue-400' : 'text-blue-600';
      case 'failed':
        return isDarkMode ? 'text-red-400' : 'text-red-600';
      default:
        return isDarkMode ? 'text-gray-400' : 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#1a9f6b]" />
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading analysis results...</p>
        </div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center">
          <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No analysis data found
          </p>
          <button
            onClick={handleBackToAnalysis}
            className={`px-4 py-2 rounded-lg transition-all ${
              isDarkMode
                ? 'bg-[#0a1414] hover:bg-[#0f1919] text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700'
            }`}
          >
            Back to Analysis Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={handleBackToAnalysis}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              isDarkMode
                ? 'bg-[#0a1414] hover:bg-[#0f1919] text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analysis Dashboard
          </button>
        </div>

        {/* User Info Bar */}
        <div className={`rounded-2xl p-4 mb-6 ${
          isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white border border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br from-green-600 to-green-400 text-white text-lg font-bold">
                {analysisData.userName
                  ? analysisData.userName.split(' ').map((n: string) => n[0]).join('').toUpperCase()
                  : ''}
              </div>
              <div>
                <h2 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {analysisData.userName}
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {analysisData.userRole}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {analysisData.totalFiles}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Files Analyzed
              </p>
            </div>
          </div>
        </div>

        {/* Files Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {analysisData.files.map((file: AnalysisFile) => (
            <div
              key={file.id}
              onClick={() => handleFileClick(file)}
              className={`relative rounded-xl p-5 cursor-pointer transition-all transform hover:scale-[1.02] ${
                isDarkMode
                  ? 'bg-[#0a1414] hover:bg-[#0f1919] border border-[#1a2424] hover:border-[#1a9f6b]'
                  : 'bg-white hover:bg-gray-50 border border-gray-200 hover:border-[#1a9f6b]'
              } ${selectedFile === file.id ? 'ring-2 ring-[#1a9f6b]' : ''}`}
            >
              {loadingFile === file.id && (
                <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center z-10">
                  <Loader2 className="w-6 h-6 text-[#1a9f6b] animate-spin" />
                </div>
              )}

              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-100'
              }`}>
                {file.type === 'audio' ? (
                  <FileAudio className="w-6 h-6 text-[#1a9f6b]" />
                ) : (
                  <FileText className="w-6 h-6 text-[#1a9f6b]" />
                )}
              </div>

              <h3 className={`font-medium mb-2 truncate ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {file.filename}
              </h3>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Customer:
                  </span>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {file.customerName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Status:
                  </span>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(file.status)}
                    <span className={`text-xs capitalize ${getStatusColor(file.status)}`}>
                      {file.status}
                    </span>
                  </div>
                </div>
                {/* Retry button for failed recordings */}
                {file.status === 'failed' && (
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileClick(file);
                      }}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-all shadow-sm border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a9f6b] ${
                        isDarkMode
                          ? 'bg-[#1a2424] text-white border-[#1a9f6b] hover:bg-[#0f1919]'
                          : 'bg-white text-[#1a9f6b] border-[#1a9f6b] hover:bg-gray-100'
                      }`}
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalysisResultsPage;
